// Stencil
import { Component, Host, h, Prop, State, Element, Watch } from "@stencil/core";
// Other Libraries
import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { formatDistanceToNow, Locale as LocaleDateFns } from "date-fns";
import { ja, zhCN, enUS } from "date-fns/locale";
// import { enUS } from "date-fns/locale";
import { ChEditCustomEvent } from "@genexus/chameleon-controls-library";
// Custom
import { Locale } from "../../common/locale";

// const locale = enUS; // use `zhCN` for Chinese, `ja` for Japanese, `enUS` for English

import { formatDate } from "../../common/helpers";

const LANGS_DATE_FNS_MAP: LanguageMap = {
  "en": enUS,
  "ja": ja,
  "zh-CN": zhCN,
  "zh-TW": zhCN,
  "zh-HK": zhCN
};

const DETAIL_ICON = getIconPath({
  category: "system",
  name: "detail",
  colorType: "primary"
});
const FILTER_ICON = getIconPath({
  category: "window-tools",
  name: "filter",
  colorType: "on-surface"
});
const KB_DEFAULT_ICON = getIconPath({
  category: "objects",
  name: "knowledge-base"
});
const SEARCH_ICON = getIconPath({
  category: "system",
  name: "search",
  colorType: "on-surface"
});

const PROJECT_NAME_PLACEHOLDER = "[projectName]";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "utils/layout",
  "components/edit",
  "components/icon",
  "components/button",
  "chameleon/scrollbar",
  "utils/typography"
];

@Component({
  tag: "gx-ide-start-page",
  styleUrl: "start-page.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/start-page"]
})
export class GxIdeStartPage {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #userLanguage: LocaleDateFns;

  @Element() el: HTMLGxIdeStartPageElement;

  /**
   * The kbs after the filter value.
   */
  @State() filteredKbs: RecentKBData[];

  @State() kbFilterValue: string = "";
  @Watch("kbFilterValue")
  kbFilterValueChanged(newKbFilterValue: string) {
    this.filteredKbs = this.kbs.filter(kb =>
      kb.name.toLowerCase().includes(newKbFilterValue.toLocaleLowerCase())
    );
  }

  /**
   * True if the start page is waiting, either for the kb's list or the secondary section.
   */
  @State() loading: boolean = true;

  /**
   * Callback invoked to create a new KB. It must be specified whether this create is create from server or not, through the parameter 'fromServer'.
   */
  @Prop() readonly createKBCallback!: (fromServer: boolean) => Promise<void>;

  /**
   * A temporary property to hide the "Open Existing Project" card.
   */
  @Prop() readonly hideOpenExistingProject: boolean = false;

  /**
   * User's kbs array
   */
  @Prop() readonly kbs: RecentKBData[];
  @Watch("kbs")
  kbsChanged(newKbs: RecentKBData[]) {
    this.filteredKbs = newKbs;
    this.kbFilterValue = ""; // clear filter
  }

  /**
   * The minimum required of kbs to display a filter.
   */
  @Prop() readonly kbsFilterThreshold: number;

  /**
   * Callback invoked to open a KB when the user clicks on a KB card.
   */
  @Prop() readonly openKbCallback!: (id: string) => Promise<void>;

  /**
   * Callback invoked when the user clicks a button to open a sample.
   */
  @Prop() readonly openSampleCallback!: (sampleId: string) => Promise<void>;

  /**
   * List of samples available to be displayed in the samples card.
   */
  @Prop() readonly samples?: SampleData[];

  /**
   * The secondary section that will appear below the "Create a new Knowledge Base" main section. This section will be displayed if there are no recent kbs.
   */
  @Prop() readonly secondarySection?: SecondarySection;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    // initialize filteredKbs
    this.kbsChanged(this.kbs);

    // get user language to display the kb "last opened" value in the appropriate language.
    const userLanguage: keyof LanguageMap = document.documentElement
      .lang as keyof LanguageMap;
    this.#userLanguage = LANGS_DATE_FNS_MAP[userLanguage];
  }

  #createKBClickHandler = (fromServer: boolean) => (event: MouseEvent) => {
    event.stopPropagation();
    this.createKBCallback(fromServer);
  };

  #filterKbs = (event: ChEditCustomEvent<string> | InputEvent) => {
    this.kbFilterValue = event.detail as string;
  };

  #openKbClickHandler = (kbId: string) => (event: MouseEvent) => {
    event.stopPropagation();
    this.openKbCallback(kbId);
  };

  // renders

  #evaluateContentToRender() {
    return this.kbs && this.kbs.length > 0
      ? this.#renderKbsSection()
      : this.#renderSecondarySection();
  }

  #renderKbsSection = () => {
    const hideKbsFilter = this.kbs.length < this.kbsFilterThreshold;
    const kbsListEmpty = this.filteredKbs.length === 0;
    let contentToRender: HTMLGxgIdeLoaderElement | HTMLElement[];

    // Evaluate what is the content to display.
    if (this.filteredKbs.length > 0) {
      contentToRender = this.#renderKbs();
    } else {
      // No kb has pass the filter. Display an empty-state.
      contentToRender = (
        <gx-ide-empty-state
          isAnimated
          stateIconSrc={FILTER_ICON}
          stateTitle={this.#componentLocale.recentKBs.noMatches}
        ></gx-ide-empty-state>
      );
    }

    return (
      <section class="section-kbs">
        <header
          class={{
            "section-kbs__header": true
          }}
        >
          <label
            class="subtitle-regular-s section-kbs__title"
            htmlFor="filter-kbs"
          >
            {this.#componentLocale.recentKBs.title}
          </label>
          <ch-edit
            // Render always, and display or hide, ro prevent FOUC, and also prevent
            // the header block-size from growing a little bit when the input renders.
            id="filter-kbs"
            value={this.kbFilterValue}
            class={{
              "input": true,
              "section-kbs__filter--hidden": hideKbsFilter
            }}
            startImgSrc={SEARCH_ICON}
            placeholder={this.#componentLocale.recentKBs.filterKbsPlaceholder}
            onInput={!hideKbsFilter && this.#filterKbs}
            disabled={hideKbsFilter}
          ></ch-edit>
        </header>

        <div
          class={{
            "section-kbs__list": true,
            "section-kbs__list--empty": kbsListEmpty,
            "scrollable": true
          }}
        >
          {this.filteredKbs.length > 0 ? this.#renderKbs() : contentToRender}
        </div>
      </section>
    );
  };

  #renderKbs = (): HTMLElement[] => {
    return this.filteredKbs?.map((kb, index) => {
      // relative time ago

      const relativeDate = kb.lastOpenedDate
        ? formatDistanceToNow(new Date(kb.lastOpenedDate), {
            addSuffix: true,
            locale: this.#userLanguage
          })
        : undefined;

      // convert date to date format, since toISOString() requires date as Date.
      const dateAsDate = kb.lastOpenedDate
        ? new Date(kb.lastOpenedDate)
        : undefined;

      let dateFormated = formatDate(dateAsDate, "date-time-short");

      // icon
      const kbIcon = kb.icon ?? KB_DEFAULT_ICON;

      return (
        <article class="kb__item" key={index}>
          <div class="kb__details">
            <ch-image class="icon-m" src={kbIcon}></ch-image>
            <div class="kb__info">
              <button
                class="body-regular-m kb__name"
                onClick={this.#openKbClickHandler(kb.id)}
              >
                {kb.name}
              </button>
              {kb.path && <p class="body-regular-xs kb__path">{kb.path}</p>}
            </div>
          </div>
          {relativeDate && (
            <p class="body-regular-s kb__modified">
              {`${this.#componentLocale.recentKBs.opened}`}{" "}
              <time dateTime={dateFormated}>{dateFormated}</time>
            </p>
          )}
        </article>
      );
    });
  };

  #renderSecondarySection = (): HTMLElement | null => {
    if (!this.secondarySection) {
      return null;
    }

    const multipleCards = this.secondarySection.cards.length > 2;

    return (
      <section
        // Recent Knowledge Bases
        class="section-no-kbs"
      >
        <header>
          <h2 class="body-regular-s section-secondary__title">
            {this.secondarySection.title}
          </h2>
        </header>

        <section
          class={{
            "section-secondary__actions": true,
            "section-secondary__actions--two-cards": !multipleCards,
            "section-secondary__actions--multiple-cards": multipleCards,
            "scrollable": true
          }}
        >
          {this.secondarySection.cards.map((card, index) => {
            return (
              <article class="card card-outlined" key={index}>
                <span class="icon-capsule">
                  <ch-image
                    class="icon-l icon-circular"
                    src={card.icon.src}
                    aria-label={card.icon.ariaLabel}
                  ></ch-image>
                </span>
                <header class="card__header">
                  <h2 class="subtitle-semi-bold-xs card__header-title">
                    {card.title}
                  </h2>
                  {card.description.map(paragraph => (
                    <p class="body-regular-s card__header-description">
                      {paragraph}
                    </p>
                  ))}
                  {card.action && (
                    <button
                      class="button-tertiary button-icon-and-text button-tertiary-no-padding-inline-start"
                      onClick={card.action.callback && card.action.callback}
                    >
                      <ch-image class="icon-m" src={DETAIL_ICON}></ch-image>
                      {card.action.caption}
                    </button>
                  )}
                </header>
              </article>
            );
          })}
        </section>
      </section>
    );
  };

  render() {
    this.loading =
      (!this.kbs || this.kbs?.length === 0) && !this.secondarySection;

    const renderViewSamplesCard =
      this.samples?.length && this.openSampleCallback;

    return (
      <Host class="scrollable">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <main class="main scrollable">
          <section
            // Create a new Knowledge Base
            class="section-create-new-kb"
          >
            <header
              // main header
              class="section-create-new-kb__header"
            >
              <h1 class="heading-5 section-create-new-kb__title">
                {this.#componentLocale.createKB.title}
              </h1>
              <p class="body-regular-m">
                {this.#componentLocale.createKB.description}
              </p>
            </header>

            <section class="section-create-new-kb__actions scrollable">
              <article
                // create project
                class="card card-filled"
              >
                <header class="card__header">
                  <div class="card__header-description">
                    <h2 class="subtitle-semi-bold-xs card__header-title">
                      {this.#componentLocale.createProject.title}
                    </h2>
                    <p class="body-regular-s">
                      {this.#componentLocale.createProject.description}
                    </p>
                  </div>
                </header>
                <footer class="card__footer">
                  <button
                    class="button-primary"
                    onClick={this.#createKBClickHandler(false)}
                  >
                    {this.#componentLocale.createProject.buttonCaption}
                  </button>
                </footer>
              </article>

              {!this.hideOpenExistingProject ? (
                <article
                  // open existing project
                  class="card-filled"
                >
                  <header class="card__header">
                    <div class="card__header-description">
                      <h2 class="subtitle-semi-bold-xs">
                        {this.#componentLocale.openExistingProject.title}
                      </h2>
                      <p class="body-regular-s">
                        {this.#componentLocale.openExistingProject.description}
                      </p>
                    </div>
                  </header>
                  <footer class="card__footer">
                    <button
                      class="button-secondary cancel-btn"
                      onClick={this.#createKBClickHandler(true)}
                    >
                      {this.#componentLocale.openExistingProject.buttonCaption}
                    </button>
                  </footer>
                </article>
              ) : null}

              {renderViewSamplesCard ? (
                <article
                  // view samples
                  class="card-filled"
                >
                  <header class="card__header">
                    <div class="card__header-description">
                      <h2 class="subtitle-semi-bold-xs">
                        {this.#componentLocale.viewSamples.title}
                      </h2>
                      <p class="body-regular-s">
                        {this.#componentLocale.viewSamples.description}
                      </p>
                    </div>
                  </header>
                  <footer class="card__footer">
                    <div class="buttons-spacer samples-buttons-container">
                      {this.samples.map(sample => {
                        return (
                          <button
                            class="button-secondary"
                            onClick={() => this.openSampleCallback(sample.id)}
                          >
                            {this.#componentLocale.viewSamples.open.replace(
                              PROJECT_NAME_PLACEHOLDER,
                              sample.name
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </footer>
                </article>
              ) : null}
            </section>
          </section>
          <section class="section-secondary scrollable">
            {this.loading ? (
              <gx-ide-loader
                class="loader"
                loaderTitle={this.#componentLocale.recentKBs.loaderTitle}
                description={this.#componentLocale.recentKBs.loaderDescription}
                show
              ></gx-ide-loader>
            ) : (
              this.#evaluateContentToRender()
            )}
          </section>
        </main>
      </Host>
    );
  }
}

export type CardInfo = {
  icon?: {
    src: string;
    ariaLabel: string;
  };
  title: string;
  description: string[];
  action?: {
    iconSrc: string;
    caption: string;
    callback?: () => void;
  };
};

type LanguageMap = {
  "en": LocaleDateFns;
  "ja": LocaleDateFns;
  "zh-CN": LocaleDateFns;
  "zh-TW": LocaleDateFns;
  "zh-HK": LocaleDateFns;
};

export type RecentKBData = {
  id?: string;
  name: string;
  lastOpenedDate?: Date;
  icon: string;
  path?: string;
};

export type SampleData = {
  id: string;
  name: string;
};

export type SecondarySection = {
  title: string;
  cards: CardInfo[];
};
